import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { superficieDoHost } from '../core/dominios';

export type Theme = 'dark' | 'light';
const KEY = 'yard.theme';

type Ctx = {
  suiteClaro?: boolean;
  theme: Theme;
  setTheme: (t: Theme) => void;
  /** Sugestão de marca, aplicada somente nas páginas públicas. */
  sugerirTema: (t: string | null | undefined) => void;
};

/** Exportado para vistas isoladas e testes, sem depender do navegador. */
export const ThemeCtx = createContext<Ctx | null>(null);

/**
 * O Suite usa sempre a interface clara. A Surface e páginas publicadas pelos
 * clientes preservam sua política anterior. Meet mantém o vídeo escuro no
 * próprio componente, sem transformar o restante da aplicação em tema escuro.
 * Esta lista espelha as rotas públicas de Router.tsx e o script de index.html.
 */
export function suiteSempreClaro(host: string, pathname: string): boolean {
  if (superficieDoHost(host)) return false;
  const hostname = host.toLowerCase().split(':')[0];
  if (hostname === 'developers.yardsuite.com.br' || hostname === 'developers.localhost') return true;
  return !/^\/(?:site|b|i|s|p|f|d|dp|l|agendar|doc)(?:\/|$)/i.test(pathname);
}

const escolhaDaPessoa = (): Theme | null => {
  try {
    const saved = localStorage.getItem(KEY);
    return saved === 'dark' || saved === 'light' ? saved : null;
  } catch {
    return null;
  }
};

function temaPublicoInicial(): Theme {
  const saved = escolhaDaPessoa();
  if (saved) return saved;
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark' : 'light';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const claro = suiteSempreClaro(window.location.host, pathname);
  const [temaPublico, set] = useState<Theme>(temaPublicoInicial);
  const theme: Theme = claro ? 'light' : temaPublico;

  // Atualiza antes de pintar uma navegação interna. O script inicial cobre
  // a primeira carga, inclusive com preferência escura já salva no navegador.
  useLayoutEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const setTheme = useCallback((t: Theme) => {
    if (claro) return;
    try { localStorage.setItem(KEY, t); } catch { /* Preferência só nesta aba. */ }
    set(t);
  }, [claro]);

  const sugerirTema = useCallback((t: string | null | undefined) => {
    if (claro || escolhaDaPessoa()) return;
    set(t === 'dark' || t === 'light' ? t : temaPublicoInicial());
  }, [claro]);

  const value = useMemo(() => ({ theme, setTheme, sugerirTema, suiteClaro: claro }), [theme, setTheme, sugerirTema, claro]);
  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeCtx);
  if (!ctx) throw new Error('useTheme fora do ThemeProvider');
  return ctx;
}

/** Mantido para a casca do suitemail deste repo, que ainda usa o rail com alternador. */
export function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  return (
    <div className="theme-toggle" role="group" aria-label="Tema da interface">
      <button type="button" aria-pressed={theme === 'dark'} onClick={() => setTheme('dark')}>
        Dark
      </button>
      <button type="button" aria-pressed={theme === 'light'} onClick={() => setTheme('light')}>
        Light
      </button>
    </div>
  );
}
